import { useState, useEffect } from "react";

const API = "https://scraps-backend.onrender.com";
const FONT = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');`;

function LeagueCard({ team, league, onClick, isAdmin, isPending }) {
  const [hovered, setHovered] = useState(false);
  const isEliminator = league.league_type === "eliminator";
  const isH2H = league.league_type === "h2h";
  const isAlive = team.status !== "eliminated";
  const creditChange = team.credits - (team.starting_credits || 5000);
  const accentColor = isEliminator ? "#dc2626" : isH2H ? "#1d4ed8" : "#3d9e7a";

  return (
    <div
      onClick={isPending ? undefined : onClick}
      onMouseEnter={() => !isPending && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isPending ? "#f9fafb" : "#ffffff",
        borderRadius: 16,
        padding: "26px 28px",
        cursor: isPending ? "default" : "pointer",
        transition: "transform 0.12s, box-shadow 0.12s",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.1)",
        opacity: isPending ? 0.7 : 1,
        position: "relative",
      }}
    >
      {/* Admin badge */}
      {isAdmin && !isPending && (
        <div style={{ position:"absolute", top:16, right:16, display:"flex", alignItems:"center", gap:4, backgroundColor:"#f3f4f6", borderRadius:6, padding:"3px 8px", border:"1px solid #e5e7eb" }}>
          <span style={{ fontSize:12 }}>⚙️</span>
          <span style={{ fontSize:10, fontWeight:700, color:"#6b7280", fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em" }}>ADMIN</span>
        </div>
      )}

      {/* Type badge + paused + pending */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        <span style={{
          fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:20,
          fontFamily:"'Inter', sans-serif", letterSpacing:"0.04em",
          background: isEliminator ? "#fff5f5" : isH2H ? "#eff6ff" : "#f0fdf8",
          color: isEliminator ? "#dc2626" : isH2H ? "#1d4ed8" : "#3d9e7a",
          border: `1px solid ${isEliminator ? "#fecaca" : isH2H ? "#93c5fd" : "#a7f3d0"}`,
        }}>
          {isEliminator ? "🎯 ELIMINATOR" : isH2H ? "⚔️ HEAD TO HEAD" : "🏆 CLASSIC"}
        </span>
        {isPending && (
          <span style={{ fontSize:10, padding:"3px 8px", borderRadius:6, background:"#fffbeb", color:"#92400e", border:"1px solid #fcd34d", fontWeight:600, fontFamily:"'Inter', sans-serif" }}>
            ⏳ PENDING APPROVAL
          </span>
        )}
        {league.paused && !isPending && (
          <span style={{ fontSize:10, padding:"3px 8px", borderRadius:6, background:"#fff7ed", color:"#c2410c", border:"1px solid #fed7aa", fontWeight:600, fontFamily:"'Inter', sans-serif" }}>
            ⏸ PAUSED
          </span>
        )}
      </div>

      {/* League name */}
      <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color: isPending ? "#9ca3af" : "#1a1a2e", marginBottom:4 }}>
        {league.name}
      </div>
      <div style={{ fontSize:12, color:"#6b7280", marginBottom: isPending ? 0 : 14, fontFamily:"'Inter', sans-serif" }}>
        Your team: <span style={{ color:"#374151", fontWeight:500 }}>{team.team_name}</span>
      </div>

      {/* Stats - only show if not pending */}
      {!isPending && (
        <div style={{ display:"flex", alignItems:"center", gap:0, paddingTop:12, borderTop:"1px solid #f3f4f6" }}>
          {isEliminator ? <>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:16, fontWeight:800, color: isAlive ? "#3d9e7a" : "#ef4444" }}>
                {isAlive ? "✓ Alive" : "✗ Out"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>status</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:16, fontWeight:800, color:"#111827" }}>
                {team.weeks_survived || 0}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>wks survived</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:16, fontWeight:800, color:"#111827" }}>
                {team.memberCount || "—"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>members</div>
            </div>
          </> : isH2H ? <>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#111827" }}>
                {team.h2h_wins || 0}-{team.h2h_losses || 0}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>record</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#111827" }}>
                #{team.h2h_rank || "—"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>rank</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#111827" }}>
                {team.memberCount || "—"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>members</div>
            </div>
          </> : <>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#3d9e7a" }}>
                {Number(team.credits).toLocaleString()}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>credits</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#111827" }}>
                #{team.rank || "—"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>rank</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:20, fontWeight:800, color:"#111827" }}>
                {team.memberCount || "—"}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>members</div>
            </div>
            <div style={{ width:1, height:32, background:"#f3f4f6" }}/>
            <div style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:13, fontWeight:700, color: creditChange >= 0 ? "#3d9e7a" : "#ef4444" }}>
                {creditChange >= 0 ? "+" : ""}{creditChange.toLocaleString()}
              </div>
              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, fontFamily:"'Inter', sans-serif" }}>vs start</div>
            </div>
          </>}
        </div>
      )}
    </div>
  );
}

export default function MyLeagues({ leagues: initialLeagues, account, onEnter, onJoin, onRequest, onLogout, accountName, adminLeagueIds: initialAdminIds, onAdminIdsLoaded }) {
  const [enriched, setEnriched] = useState([]);
  const [adminLeagueIds, setAdminLeagueIds] = useState(initialAdminIds || []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const enrich = async () => {
      setLoading(true);
      let leaguesToEnrich = initialLeagues || [];
      let adminIds = [];
      const token = localStorage.getItem("scraps_token");
      if (token) {
        try {
          const res = await fetch(`${API}/auth/login-refresh`, {
            method:"POST",
            headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          });
          if (res.ok) {
            const data = await res.json();
            if (data.leagues && data.leagues.length > 0) leaguesToEnrich = data.leagues;
            if (data.admin_league_ids) adminIds = data.admin_league_ids;
          }
        } catch(e) {}
      }
      setAdminLeagueIds(adminIds);
      if (onAdminIdsLoaded && adminIds.length > 0) onAdminIdsLoaded(adminIds);

      const results = [];
      for (const { team, league } of leaguesToEnrich) {
        let enrichedTeam = { ...team };
        // Only enrich active/approved leagues
        if (league.status === "pending") {
          results.push({ team: enrichedTeam, league: {...league, league_type: league.league_type || "classic"} });
          continue;
        }
        try {
          if (league.league_type === "h2h") {
            const standingsRes = await fetch(`${API}/h2h/${league.id}/standings`).then(r => r.json());
            const myStanding = standingsRes.find(s => s.team_id === team.id);
            if (myStanding) {
              enrichedTeam.h2h_wins = myStanding.wins;
              enrichedTeam.h2h_losses = myStanding.losses;
              enrichedTeam.h2h_rank = standingsRes.findIndex(s => s.team_id === team.id) + 1;
            }
          } else if (league.league_type === "eliminator") {
            const survivors = await fetch(`${API}/eliminator/${league.id}/survivors`).then(r => r.json());
            const myEntry = survivors.find(s => s.id === team.id);
            if (myEntry) enrichedTeam = { ...enrichedTeam, ...myEntry };
          } else if (!league.league_type || league.league_type === "classic") {
            const board = await fetch(`${API}/league/${league.id}/leaderboard`).then(r => r.json());
            const myEntry = board.find(e => e.team_id === team.id);
            if (myEntry) enrichedTeam = { ...enrichedTeam, rank: myEntry.rank, credits: myEntry.credits };
          }
          const teams = await fetch(`${API}/league/${league.id}/teams`).then(r => r.json());
          enrichedTeam.memberCount = teams.length;
        } catch(e) {}
        results.push({ team: enrichedTeam, league: {...league, league_type: league.league_type || "classic"} });
      }
      setEnriched(results);
      setLoading(false);
    };
    enrich();
  }, [initialLeagues, account]);

  const activeLeagues = enriched.filter(({league}) => league.status !== "pending");
  const pendingLeagues = enriched.filter(({league}) => league.status === "pending");
  const classicLeagues = activeLeagues.filter(({league}) => league.league_type === "classic" || (!league.league_type));
  const eliminatorLeagues = activeLeagues.filter(({league}) => league.league_type === "eliminator");
  const h2hLeagues = activeLeagues.filter(({league}) => league.league_type === "h2h");

  const initials = accountName ? accountName.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() : "?";

  return (
    <div style={{ minHeight:"100vh", background:"#0d1b2a", fontFamily:"'Inter', sans-serif" }}>
      <style>{FONT}</style>

      {/* Top bar */}
      <div style={{ background:"#0d1b2a", borderBottom:"1px solid rgba(255,255,255,0.08)", padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ background:"#ffffff", borderRadius:8, padding:"3px 10px", display:"inline-block" }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:26, objectFit:"contain", display:"block" }}/>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {window.innerWidth >= 640 && <div style={{ fontSize:13, color:"#6b7280" }}>Hey, <span style={{ color:"#f0f4ff", fontWeight:500 }}>{accountName}</span></div>}
          <div style={{ width:32, height:32, borderRadius:"50%", background:"#3d9e7a", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#fff" }}>{initials}</div>
          <button onClick={onLogout} style={{ padding:"5px 12px", background:"transparent", border:"1px solid rgba(255,255,255,0.1)", borderRadius:6, color:"#6b7280", cursor:"pointer", fontSize:12, fontFamily:"'Inter', sans-serif" }}>Logout</button>
        </div>
      </div>

      <div style={{ maxWidth:900, margin:"0 auto", padding:"32px 24px" }}>
        <div style={{ fontFamily:"'Inter', sans-serif", fontSize:32, fontWeight:800, color:"#ffffff", marginBottom:4 }}>My Leagues</div>
        <div style={{ fontSize:14, color:"#6b7280", marginBottom:28 }}>Click any league to enter</div>

        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:"#6b7280" }}>
            <div style={{ fontSize:24, marginBottom:12 }}>⟳</div>
            <div style={{ fontFamily:"'Inter', sans-serif", fontSize:14 }}>Loading your leagues...</div>
          </div>
        ) : (
          <>
            {/* Pending leagues */}
            {pendingLeagues.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#f0f4ff", letterSpacing:"0.08em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>⏳</span> PENDING APPROVAL
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(360px, 1fr))", gap:14 }}>
                  {pendingLeagues.map(({team, league}) => (
                    <LeagueCard key={league.id} team={team} league={league} isPending={true} isAdmin={adminLeagueIds.includes(league.id)} />
                  ))}
                </div>
              </div>
            )}

            {classicLeagues.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#f0f4ff", letterSpacing:"0.08em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>🏆</span> CLASSIC LEAGUES
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(360px, 1fr))", gap:14 }}>
                  {classicLeagues.map(({team, league}) => (
                    <LeagueCard key={league.id} team={team} league={league} onClick={() => onEnter({team, league})} isAdmin={adminLeagueIds.includes(league.id)} />
                  ))}
                </div>
              </div>
            )}

            {eliminatorLeagues.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#f0f4ff", letterSpacing:"0.08em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>🎯</span> ELIMINATOR LEAGUES
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(360px, 1fr))", gap:14 }}>
                  {eliminatorLeagues.map(({team, league}) => (
                    <LeagueCard key={league.id} team={team} league={league} onClick={() => onEnter({team, league})} isAdmin={adminLeagueIds.includes(league.id)} />
                  ))}
                </div>
              </div>
            )}

            {h2hLeagues.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#f0f4ff", letterSpacing:"0.08em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>⚔️</span> HEAD TO HEAD LEAGUES
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(360px, 1fr))", gap:14 }}>
                  {h2hLeagues.map(({team, league}) => (
                    <LeagueCard key={league.id} team={team} league={league} onClick={() => onEnter({team, league})} isAdmin={adminLeagueIds.includes(league.id)} />
                  ))}
                </div>
              </div>
            )}

            {enriched.length === 0 && (
              <div style={{ textAlign:"center", padding:"48px 24px", background:"rgba(255,255,255,0.03)", borderRadius:14, border:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize:40, marginBottom:16 }}>🏈</div>
                <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:20, fontWeight:700, color:"#f0f4ff", marginBottom:8 }}>NO LEAGUES YET</div>
                <div style={{ fontSize:14, color:"#6b7280" }}>Join a league with an invite code or request your own</div>
              </div>
            )}

            {/* Join/Request buttons */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:32, paddingTop:24, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
              <button onClick={onJoin} style={{ padding:"18px 16px", background:"rgba(255,255,255,0.05)", border:"2px solid rgba(255,255,255,0.12)", borderRadius:14, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600, color:"#f0f4ff", textAlign:"center" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}>
                <div style={{ fontSize:28, marginBottom:8 }}>🔗</div>
                <div style={{ fontSize:14 }}>Join a League</div>
                <div style={{ fontSize:11, color:"#6b7280", marginTop:4, fontWeight:400 }}>Have an invite code?</div>
              </button>
              <button onClick={onRequest} style={{ padding:"18px 16px", background:"rgba(255,255,255,0.05)", border:"2px solid rgba(255,255,255,0.12)", borderRadius:14, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600, color:"#f0f4ff", textAlign:"center" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}>
                <div style={{ fontSize:28, marginBottom:8 }}>🏆</div>
                <div style={{ fontSize:14 }}>Start a League</div>
                <div style={{ fontSize:11, color:"#6b7280", marginTop:4, fontWeight:400 }}>Build your own</div>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
